<template>
  <div class="employee-agreements">
    <Loader v-if="!inited" :active="!inited" label="Данные загружаются" :height="400" class="mt-4"></Loader>

    <template v-else>
      <div v-if="empty" class="employee-agreements__empty">Договоров для печати пока нет</div>

      <div v-else>
        <div class="employee-agreements__filters agreements-filters">
          <c-button2
            v-if="filtersOpen"
            class="agreements-filters__toggler"
            label="Скрыть фильтры"
            @click="filtersOpen = false"
          />

          <transition name="slide">
            <div v-show="filtersOpen" class="agreements-filters__slide">
              <div class="agreements-filters__wrapper">
                <c-input
                  ref="periodPicker"
                  v-model="filters.period"
                  class="agreements-filters__item agreements-filters__period"
                  label="Период договора"
                  placeholder="00.00.0000 - 00.00.0000"
                  type="period-without-fixed-intervals"
                  icon="calendar"
                  :clearable="true"
                  :icon-right="true"
                />
                <c-input
                  v-model="filters.company"
                  class="agreements-filters__item agreements-filters__company"
                  :type="'select'"
                  label="Юридическое лицо"
                  :searchable="true"
                  :placeholder="'Любое'"
                  :clearable="true"
                  :items="collections.companies"
                />
                <c-select
                  v-model="filters.types"
                  class="agreements-filters__item agreements-filters__type"
                  type="select"
                  label="Тип договора"
                  :searchable="true"
                  :multiple="true"
                  :items="collections.agreementTypes"
                  :groups="collections.agreementGroups"
                  placeholder="Все"
                />
              </div>
              <div class="agreements-filters__actions">
                <c-button size="large" class="agreements-filters__reset" label="Сбросить" @click="resetFilters" />
                <c-button
                  class="agreements-filters__apply"
                  label="Применить"
                  color="blue"
                  size="large"
                  @click="applyFilters"
                />
              </div>
            </div>
          </transition>

          <c-button2
            v-if="filtersOpen"
            class="agreements-filters__toggler"
            label="Скрыть фильтры"
            @click="filtersOpen = false"
          />

          <c-button2
            v-if="!filtersOpen"
            class="agreements-filters__toggler"
            label="Показать фильтры"
            @click="filtersOpen = true"
          />
        </div>

        <Loader v-if="loading" :active="loading" label="Данные загружаются" :height="400"></Loader>

        <scrolled-table v-if="!loading" class="lg">
          <table class="h-table agreements-table">
            <thead>
              <tr>
                <th width="500">
                  <div class="h-table__header">
                    <div
                      class="h-table__col-title"
                      :class="{
                        active: sortBy === 'company_name',
                      }"
                      @click="sort('company_name')"
                    >
                      Юридическое лицо
                    </div>
                  </div>
                </th>
                <th width="1400">
                  <div class="h-table__header">
                    <div
                      class="h-table__col-title"
                      :class="{
                        active: sortBy === 'agreement_name',
                      }"
                      @click="sort('agreement_name')"
                    >
                      Договор
                    </div>
                  </div>
                </th>
                <th class="agreements-table__actions"></th>
              </tr>
            </thead>

            <template v-for="position in positions">
              <tr
                v-if="positions.length > 1"
                :key="position.id"
                class="category"
                :class="{
                  open: !position.closed,
                }"
              >
                <td class="category__header" colspan="3">
                  <div class="category__wrapper">
                    <span class="category__toggler" @click="$set(position, 'closed', !position.closed)">
                      <svg-icon name="chevron-top-circled" :size="24" color="primary"></svg-icon>
                    </span>
                    <div class="category__name">
                      {{ position.name }}
                      <span class="category__count">{{ position.items.length }}</span>
                    </div>
                  </div>
                </td>
              </tr>

              <transition :key="position.id + '-wrapper'" name="slide">
                <tbody v-if="!position.closed" :key="position.id + '-items'" class="slide-transition">
                  <tr v-for="item in position.items" :key="position.id + '-' + item.id" class="agreement">
                    <td>
                      {{ item.company_name }}
                    </td>
                    <td>
                      <div class="agreement__name">
                        {{ item.agreement_name }}
                      </div>
                      <div v-if="item.agreement_contract_type_name" class="agreement__category">
                        {{ item.agreement_contract_type_name }}
                      </div>
                    </td>
                    <td>
                      <div class="agreement-actions">
                        <button
                          v-b-popover.html="{
                            container: '#app',
                            trigger: 'hover blur',
                            content: 'Распечатать договор',
                            placement: 'top',
                          }"
                          class="agreement-action"
                          type="button"
                          @click="print(item)"
                        >
                          <svg-icon name="print" :size="16"></svg-icon>
                        </button>
                        <button
                          v-b-popover.html="{
                            container: '#app',
                            trigger: 'hover blur',
                            content: 'Скачать в формате PDF',
                            placement: 'top',
                          }"
                          class="agreement-action"
                          type="button"
                          @click="toPdf(item)"
                        >
                          <svg-icon name="pdf" :size="16"></svg-icon>
                        </button>
                        <button
                          v-b-popover.html="{
                            container: '#app',
                            trigger: 'hover blur',
                            content: 'Скачать в формате Microsoft Word',
                            placement: 'top',
                          }"
                          class="agreement-action"
                          type="button"
                          @click="toDoc(item)"
                        >
                          <svg-icon name="word" :size="16"></svg-icon>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </transition>
            </template>
          </table>
        </scrolled-table>

        <div v-if="!loading" class="sm">
          <div class="agreements-list">
            <div class="agreements-list__head">Юридическое лицо/Договор</div>
            <div class="agreements-list__positions">
              <div v-for="position in positions" :key="position.id" class="agreements-list__position category">
                <div class="category__header">
                  <div class="category__wrapper">
                    <span class="category__toggler" @click="$set(position, 'closed', !position.closed)">
                      <svg-icon name="chevron-top-circled" :size="24" color="primary"></svg-icon>
                    </span>
                    <div class="category__name">
                      {{ position.name }}
                      <span class="category__count">{{ position.items.length }}</span>
                    </div>
                  </div>
                </div>
                <transition :key="position.id + '-wrapper'" name="slide">
                  <div v-show="!position.closed" class="agreements-list__items">
                    <div
                      v-for="item in position.items"
                      :key="item.id"
                      class="agreements-list__item agreements-list-item"
                    >
                      <div class="agreements-list-item__company">
                        {{ item.company_name }}
                      </div>
                      <div class="agreements-list-item__name">
                        {{ item.agreement_name }}
                      </div>
                      <div v-if="item.agreement_contract_type_name" class="agreements-list-item__category">
                        {{ item.agreement_contract_type_name }}
                      </div>
                      <div class="agreements-list-item__actions">
                        <button
                          v-b-popover.html="{
                            container: '#app',
                            trigger: 'hover blur',
                            content: 'Распечатать договор',
                            placement: 'top',
                          }"
                          class="agreement-action"
                          type="button"
                          @click="print(item)"
                        >
                          <svg-icon name="print" :size="16"></svg-icon>
                        </button>
                        <button
                          v-b-popover.html="{
                            container: '#app',
                            trigger: 'hover blur',
                            content: 'Скачать в формате PDF',
                            placement: 'top',
                          }"
                          class="agreement-action"
                          type="button"
                          @click="toPdf(item)"
                        >
                          <svg-icon name="pdf" :size="16"></svg-icon>
                        </button>
                        <button
                          v-b-popover.html="{
                            container: '#app',
                            trigger: 'hover blur',
                            content: 'Скачать в формате Microsoft Word',
                            placement: 'top',
                          }"
                          class="agreement-action"
                          type="button"
                          @click="toDoc(item)"
                        >
                          <svg-icon name="word" :size="16"></svg-icon>
                        </button>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!loading && !positions.length" class="employee-agreements__no-results">Записи не найдены</div>
      </div>
    </template>
    <top-button root=".layout"></top-button>
  </div>
</template>
<script>
import Loader from "@/components/loader";
import TopButton from "@/components/top-button";
import ScrolledTable from "@/components/scrolled-table";
import CInput from "@/components/controls2/Input";
import CSelect from "@/components/controls2/Select";
import CButton2 from "@/components/controls2/Button";
import CButton from "@/components/controls/button";
import SvgIcon from "@/components/svg-icon";

import { getRange } from "@/utils/date/ranges";

export default {
  components: {
    Loader,
    ScrolledTable,
    CInput,
    CSelect,
    CButton,
    CButton2,
    TopButton,
    SvgIcon,
  },
  data() {
    return {
      workerId: null,
      loading: false,
      empty: false,
      inited: false,
      positions: [],
      filtersOpen: false,
      filters: {
        period: "",
        periodStart: "",
        periodEnd: "",
        company: null,
        types: [],
      },
      collections: {
        companies: [],
        agreementTypes: [],
        agreementGroups: [],
      },
      sortBy: "agreement_name",
      desc: false,
    };
  },

  watch: {
    "inited": function (v) {
      if (v) this.load();
    },
    "filters.period": function () {
      if (this.$refs.periodPicker) {
        this.filters.periodStart = this.$refs.periodPicker.period.start.input;
        this.filters.periodEnd = this.$refs.periodPicker.period.end.input;
      }
    },
  },
  created() {
    this.workerId = this.$route.params.id;

    Promise.all([this.loadCollections(), this.load("withoutFilters")]).then(() => {
      if (!this.positions.length) this.empty = true;
      this.setDefaultParams();
      this.$nextTick(() => {
        this.setPeriod();
      });
      this.inited = true;
    });
  },

  mounted() {
    this.setPeriod();
  },

  methods: {
    loadCollections() {
      return Promise.all([
        this.$store
          .dispatch("personalEmployee/getLegalEntities", {
            workerId: this.workerId,
          })
          .then((items) => {
            this.collections.companies = items.map((i) => ({
              value: i.id,
              label: i.short_name,
            }));
          }),
        this.$store.dispatch("personalEmployee/getAgreementTypes", {
            workerId: this.workerId,
          }).then((items) => {
          const groups = items.map((i) => ({
            name: i.id,
            label: i.name === "Без категории" ? "Прочее" : i.name,
          }));
          const types = [];

          items.forEach((group) => {
            group.agreements.forEach((i) => {
              types.push({
                value: i.id,
                label: i.name,
                group: group.id,
              });
            });
          });

          console.log(groups, types);

          this.collections.agreementTypes = types;
          this.collections.agreementGroups = groups;
        }),
      ]);
    },
    setDefaultParams() {
      const sp = this.$route.query;
      if (sp.periodStart && sp.periodEnd) {
        this.filters.periodStart = sp.periodStart;
        this.filters.periodEnd = sp.periodEnd;
        this.filters.period = getRange(this.filters.periodStart, this.filters.periodEnd);
      }
      if (sp.company) {
        this.filters.company = this.collections.companies.find((i) => i.value.toString() === sp.company.toString());
      }
      if (sp.types) {
        this.filters.types = sp.types
          .split(",")
          .map((type) => this.collections.agreementTypes.find((i) => i.value.toString() === type.toString()))
          .filter(Boolean);
      }
      if (sp.sortBy) {
        this.sortBy = sp.sortBy;
        this.desc = sp.desc.toString() === "1";
      }
    },
    setPeriod() {
      if (this.filters.periodStart && this.filters.periodEnd) {
        this.$refs.periodPicker.period.start.input = this.filters.periodStart;
        this.$refs.periodPicker.period.end.input = this.filters.periodEnd;

        const range = getRange(this.filters.periodStart, this.filters.periodEnd);
        this.$refs.periodPicker.dateValue = range;
      }
    },

    getParams() {
      return {
        types: this.filters.types.map((t) => t.value),
        company: this.filters.company && this.filters.company.value,
        periodStart: this.filters.periodStart,
        periodEnd: this.filters.periodEnd,
        sortBy: this.sortBy,
        desc: this.desc,
      };
    },
    saveParams() {
      const { query } = this.$route;
      const sp = {
        ...query,
        periodStart: this.filters.periodStart,
        periodEnd: this.filters.periodEnd,
        company: this.filters.company && this.filters.company.value,
        types: this.filters.types.map((i) => i.value).join(","),
        sortBy: this.sortBy,
        desc: this.desc ? 1 : 0,
      };

      this.$router
        .replace({
          name: "personal-employee-agreements",
          query: sp,
        })
        .catch(() => {});
    },

    load(withoutFilters) {
      if (this.loading) return Promise.reject();

      this.loading = true;
      this.positions = [];

      let params = {
        workerId: this.workerId,
      };

      if (!withoutFilters) {
        params = {
          ...params,
          ...this.getParams(),
        };
        this.saveParams();
      }

      return this.$store.dispatch("personalEmployee/getEmployeeAgreements", params).then((items) => {
        let posts = {};

        items.forEach((item) => {
          const post = item.post_code;
          if (!(post in posts)) {
            posts[post] = {
              id: post,
              name: item.post_name,
              items: [],
            };
          }

          posts[post].items.push(item);
        });

        posts = Object.values(posts);
        posts.sort((a, b) => (a.name < b.name ? -1 : 1));

        this.positions = posts;

        this.loading = false;
      });
    },

    sort(sortBy) {
      if (this.sortBy === sortBy) {
        this.desc = !this.desc;
      } else {
        this.desc = false;
        this.sortBy = sortBy;
      }
      this.applyFilters();
    },

    applyFilters() {
      this.load();
    },

    resetFilters() {
      this.filters.period = "";
      this.$refs.periodPicker.periodReset();
      this.filters.company = null;
      this.filters.types = [];
      this.$nextTick(() => this.applyFilters());
    },

    toDoc(agreement) {
      const queryParams = {
        r: "print-contract%2Fword",
        worker_id: this.workerId,
        contract_id: agreement.agreement_id,
        post_code: agreement.post_code,
        start_date: this.filters.periodStart,
        end_date: this.filters.periodEnd,
        company_id: agreement.company_id,
      };

      const query = Object.entries(queryParams)
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

      // eslint-disable-next-line no-restricted-globals
      location.href = `/index.php?${query}`;
    },
    toPdf(agreement) {
      const queryParams = {
        r: "print-contract%2Fpdf",
        worker_id: this.workerId,
        contract_id: agreement.agreement_id,
        post_code: agreement.post_code,
        start_date: this.filters.periodStart,
        end_date: this.filters.periodEnd,
        company_id: agreement.company_id,
      };

      const query = Object.entries(queryParams)
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

      // eslint-disable-next-line no-restricted-globals
      location.href = `/index.php?${query}`;
    },
    print(agreement) {
      console.log(agreement);
      const queryParams = {
        r: "print-contract%2Fprint",
        worker_id: this.workerId,
        contract_id: agreement.agreement_id,
        post_code: agreement.post_code,
        start_date: this.filters.periodStart,
        end_date: this.filters.periodEnd,
        company_id: agreement.company_id,
      };

      const query = Object.entries(queryParams)
        .map(([key, value]) => `${key}=${value}`)
        .join("&");

      window.open(`/index.php?${query}`, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";
.employee-agreements {
  &__empty {
    color: $color-label;
    font-size: 13px;
    margin-top: 40px;
  }
  &__no-results {
    color: $color-label;
    font-size: 13px;
    border: 1px solid #dfe4ef;
    background: white;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: -13px;
    margin-right: 2px;
  }
  .agreements-filters {
    margin-top: 40px;
    margin-bottom: 30px;
    &__wrapper {
      display: flex;
      flex-wrap: wrap;
      margin-left: -15px;
      margin-right: -15px;
    }

    &__toggler {
      display: none;

      &:focus,
      &:hover {
        border-color: #d3d8df !important;
      }
    }

    &__item {
      padding-left: 15px;
      padding-right: 15px;
      margin-bottom: 24px;
    }

    &__period {
      width: 25%;
      /deep/ {
        .c-input-container {
          transform: none;
          left: 0;
          @media screen and (max-width: 805px) {
            left: -30px;
          }
          @media screen and (max-width: 767px) {
            left: 0;
          }
        }
        .c-input-action_clear {
          margin-top: -2px;
        }
      }
    }

    &__company {
      width: 25%;
    }

    &__type {
      width: 50%;
    }

    &__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    &__reset {
      margin-right: 10px;

      & /deep/ .c-button__wrapper {
        padding-left: 0;
        padding-right: 0;
        width: 120px;
        justify-content: center;
      }
    }

    &__apply {
      & /deep/ .c-button__wrapper {
        padding-left: 0;
        padding-right: 0;
        width: 130px;
        justify-content: center;
      }
    }

    @include mobileAndTablet {
      &__period {
        width: 245px;
        flex-shrink: 0;
      }
      &__company {
        width: calc(100% - 245px);
      }
      &__type {
        width: 100%;
      }
    }

    @include onlyStandartMobile {
      margin-bottom: 30px;

      &__toggler {
        display: block;
        width: 100%;
        height: 30px;
        font-size: 12px;
        margin-bottom: 20px;
      }

      &__item {
        width: 100%;
      }

      &__actions {
        margin-bottom: 20px;
      }

      &__apply,
      &__reset {
        flex-grow: 1;
        flex-basis: 100%;

        & /deep/ .c-button__wrapper {
          width: 100%;
        }
      }

      &__apply {
        margin-left: 8px;
      }

      &__reset {
        margin-right: 8px;
      }
    }

    @media screen and (min-width: 768px) {
      &__slide {
        display: block !important;
      }
    }
  }
  .sm {
    display: none;
    margin-left: -20px;
    margin-right: -20px;
  }
  /deep/ {
    .scrolled-table {
      &__container {
        padding-right: 2px;
      }
      th:last-child {
        border-right: 1px solid #dfe4ef !important;
      }
    }
  }
  .category {
    &__header {
      background: #f5f7fa;
    }

    &__name {
      font-size: 13px;
      font-weight: 700;
    }

    &__wrapper {
      display: flex;
      align-items: center;
    }

    &__toggler {
      margin-right: 10px;
      transition: transform 250ms ease-in-out;
      cursor: pointer;
    }

    &.open {
      .category__toggler {
        transform: rotate(180deg);
      }
    }

    &__count {
      color: #8e9cbb;
      font-size: 13px;
      font-weight: 400;
      margin-left: 8px;
    }
  }
  .agreement {
    &-actions {
      display: flex;
      margin: 0 -4px;
    }
    &-action {
      margin: 0 8px;
      padding: 0;
      box-shadow: none;
      border: none;
      outline: none;
      background-color: transparent;
    }
    &__category {
      color: #8e9cbb;
      font-size: 11px;
      //margin-top: 4px;
    }
  }
  .slide-transition.slide-leave-active,
  .slide-transition.slide-enter-active {
    td {
      padding-top: 0;
      padding-bottom: 0;
    }
    display: table-row-group !important;
    .agreement-actions {
      display: none !important;
    }
  }
  .agreements-table {
    &__actions {
      width: 95px;
      max-width: 95px;
    }
  }
  .agreements-list {
    &__head {
      border: 1px solid #dfe4ef;
      min-height: 36px;
      display: flex;
      align-items: center;
      color: #8e9cbb;
      font-size: 12px;
      font-weight: 500;
      padding-left: 10px;
      padding-right: 10px;
    }
    &__positions {
    }
    .category {
      margin-bottom: -1px;
      &__header {
        padding-left: 20px;
        padding-right: 20px;
        min-height: 38px;
        display: flex;
        align-items: center;
        border-bottom: 1px solid #dfe4ef;
        border-top: 1px solid #dfe4ef;
      }
    }

    &-item {
      font-size: 13px;
      border-bottom: 1px solid #dfe4ef;
      padding: 12px 10px 13px;
      line-height: 1.4;

      &__company {
        margin-bottom: 10px;
      }
      &__category {
        color: #8e9cbb;
        font-size: 11px;
        //margin-top: 4px;
      }
      &__actions {
        margin-left: -8px;
        margin-right: -8px;
        margin-top: 12px;
      }
    }
  }

  @include mobileAndTablet {
    padding-bottom: 40px;
  }

  @include onlyMobile {
    .sm {
      display: block;
    }
    .lg {
      display: none;
    }
    &__empty {
      margin-top: 35px;
    }
    &__no-results {
      margin: 0 -21px;
    }
    .agreements-list__head {
      border-left: none;
      border-right: none;
    }
  }
}
</style>
